/** @prettier */

import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {format, utcToZonedTime} from 'date-fns-tz';
import {Grid, Box, Stack, Card, CardContent} from '@mui/material';
import React from 'react';
import {Auth} from 'aws-amplify';
import {useNavigate, useParams} from 'react-router-dom';

import {routeList} from '../PrivateRoute';
import ApplyCouponDialog from './ApplyCouponDialog';
import TransferConsentDialog from './TransferConsentDialog';
import UpdateVATNumberDialog from './UpdateVATNumberDialog';
import SettingVATNumberDialog from './SettingVATNumberDialog';
import AuthorizationTransferDialog from './AuthorizationTransferDialog';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import {PrimaryButton} from '../../component/BasicButton';
import APIErrorMessage from '../../component/APIErrorMessage';
import {iconBlack} from '../../define';

//interface import
import * as apiIF from '../../Type/api';
//MTH component import
import CardContents from '../../component/CardContents';
//API import
import {
  getCompositionsGateway,
  getBillingCustomerPortal,
  getCompositionsSitesBySiteID,
  getSettingUser,
} from '../../API/API_Collection';
import TransferMessageDialog from './TransferMessageDialog';
import {monthAbbreviationTranslation} from '../../functions/convertString';

/**
 * ゲートウェイの課金情報一覧
 * @returns JSX
 */
const BillingInformationTable = () => {
  const trans = useTranslation().t; //翻訳関数
  const [gwInfo, setGWInfo] = useState<apiIF.res.getCompositionsGateway>(); //サイト内のゲートウェイ情報
  const [selectedGW, setSelectedGW] = useState(''); //選択したゲートウェイのID
  const [userID, setUserID] = useState<string>(''); //ユーザID
  const [userEmail, setUserEmail] = useState<string>(''); //ユーザのEメールアドレス
  const [timezone, setTimezone] = useState<string>(''); //タイムゾーン
  const [isOpenAddVATNumberDialog, setIsOpenAddVATNumberDialog] =
    useState(false); //VAT番号登録ダイアログ表示フラグ
  const [isOpenTransferDialog, setIsOpenTransferDialog] = useState(false); //権限譲渡ダイアログ表示フラグ
  const [isOpenConsentDialog, setIsOpenConsentDialog] = useState(false); //権限譲渡承認ダイアログ表示フラグ
  const [isOpenCouponDialog, setIsOpenCouponDialog] = useState(false); //クーポン適用ダイアログ表示フラグ
  const [isOpenUpdateVATNumberDialog, setIsOpenUpdateVATNumberDialog] =
    useState(false); //VAT番号更新ダイアログ表示フラグ
  const [isTransferMessageDialog, setIsTransferMessageDialog] = useState(false); //メッセージダイアログ表示フラグ
  const [message, setMessage] = useState(''); //ダイアログに表示する文字列
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  /** サイトID */
  const {siteID} = useParams();
  const navigate = useNavigate();
  const [disabledButtons, setDisabledButtons] = useState<{
    [key: string]: boolean; //ポータルボタン無効化フラグ
  }>({});

  /** 月名（省略形）一覧 */
  const monthAbbreviationTable = [
    monthAbbreviationTranslation('january', trans),
    monthAbbreviationTranslation('february', trans),
    monthAbbreviationTranslation('march', trans),
    monthAbbreviationTranslation('april', trans),
    monthAbbreviationTranslation('may', trans),
    monthAbbreviationTranslation('june', trans),
    monthAbbreviationTranslation('july', trans),
    monthAbbreviationTranslation('august', trans),
    monthAbbreviationTranslation('september', trans),
    monthAbbreviationTranslation('october', trans),
    monthAbbreviationTranslation('november', trans),
    monthAbbreviationTranslation('december', trans),
  ];

  useEffect(() => {
    // ユーザIDの取得
    Auth.currentAuthenticatedUser().then(response => {
      setUserID(response['username']);
      setUserEmail(response['attributes']['email']);
    });
  }, []);

  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }
    try {
      // タイムゾーン・権限の取得
      getCompositionsSitesBySiteID(siteID)
        .then(response => {
          //admin以外がアクセスしたらリダイレクト
          if (response['site-authority'] !== 'admin') {
            navigate(routeList.mySite.path);
          }
          setTimezone(response.timezone);
        })
        .catch(() => {
          //サイト情報取得に失敗したらリダイレクト
          navigate(routeList.mySite.path);
        });
    } catch {
      return undefined;
    }

    //ゲートウェイ情報取得
    getCompositionsGateway(siteID, {
      'max-result': 10,
    }).then(gwInfo => {
      setGWInfo(gwInfo);
    });
  }, [siteID]);

  /**
   * ゲートウェイ一覧 データ部表示
   * @param gwInfo ゲートウェイの情報
   * @returns JSX
   */
  const GatewayBillingData = (props: {
    gwInfo: apiIF.resParameters.gatewayInformations;
  }) => {
    // サイトIDが取得できない場合は処理しない
    if (siteID === undefined) {
      return <></>;
    }
    //必要な情報が取得できない場合は処理しない
    if (
      props.gwInfo['billing-expiration-date'] === undefined ||
      props.gwInfo['billing-status'] === undefined ||
      props.gwInfo['billing-transfer-user'] === undefined
    ) {
      return <></>;
    }

    const localDate = utcToZonedTime(
      props.gwInfo['billing-expiration-date'],
      timezone
    );
    /**
     * 歯車メニューリスト
     * @param gwID ゲートウェイID
     * @returns メニュー一覧
     */
    const gearMenuItem = (gwID: string): GearMenuListArgType['menuItem'] => {
      return [
        // 権限譲渡
        {
          label: trans('BillingSetting.menuItem.authorization'),
          onClick: () => {
            openTransferDialog(gwID);
          },
        },
        // クーポン適用
        {
          label: trans('BillingSetting.menuItem.coupon'),
          onClick: () => {
            openApplyCouponDialog(gwID);
          },
        },
        // VAT番号更新
        {
          label: trans('BillingSetting.menuItem.updateVATNumber'),
          onClick: () => {
            openUpdateVATNumberDialog(gwID);
          },
        },
      ];
    };
    /**
     * 権限譲渡ボタン押下時処理
     * @param gwID ゲートウェイID
     */
    const openTransferDialog = (gwID: string) => {
      setSelectedGW(gwID);
      setIsOpenTransferDialog(true);
    };
    /**
     * クーポン適用ボタン押下時処理
     * @param gwID ゲートウェイID
     */
    const openApplyCouponDialog = (gwID: string) => {
      setSelectedGW(gwID);
      setIsOpenCouponDialog(true);
    };
    /**
     * 権限譲渡承諾ボタン押下時処理
     * @param gwID ゲートウェイID
     */
    const openConsentDialog = async (gwID: string) => {
      try {
        const userInfo = await getSettingUser();
        //ユーザ所在国が未設定の場合は、エラー表示
        if (userInfo.country === '') {
          setMessage(
            `${trans('TransferConsentDialog.alertMsg.countryNotSelected')}`
          );
          //エラーダイアログを開く
          setIsTransferMessageDialog(true);
          return;
        }
        setSelectedGW(gwID);
        setIsOpenConsentDialog(true);
      } catch {
        setIsOpenErrorDialog(true);
      }
    };
    /**
     * VAT番号更新ボタン押下時処理
     * @param gwID ゲートウェイID
     */
    const openUpdateVATNumberDialog = (gwID: string) => {
      setSelectedGW(gwID);
      setIsOpenUpdateVATNumberDialog(true);
    };

    // 課金状態表示の設定
    const BillingStatus = (props: {status: string}) => {
      // 課金状態によって表示内容を切り替える
      switch (props.status) {
        case 'paid': //fall through
        case 'unpaid':
          return (
            <Grid
              container
              alignItems={'center'}
              className="billingTable__data__status__textBox">
              <Grid item xs={12}>
                {trans(`BillingSetting.status.${props.status}`)}
              </Grid>
            </Grid>
          );
        case 'failed': //fall through
        case 'canceled':
          return (
            <Grid
              container
              alignItems={'center'}
              className="billingTable__data__status__textBox">
              <Grid item xs={12}>
                {trans(`BillingSetting.status.${props.status}`)}
              </Grid>
              <Grid item xs={12}>
                {`(${trans(
                  `BillingSetting.status.${props.status}Description`
                )})`}
              </Grid>
            </Grid>
          );
        default:
          return <></>;
      }
    };

    // 課金設定ボタンの表示内容決定
    const BillingSettingButton = (props: {
      gwInfo: apiIF.resParameters.gatewayInformations;
    }) => {
      // 未課金の場合
      if (props.gwInfo['billing-status'] === 'unpaid') {
        // paymentボタンのみ
        return (
          <PrimaryButton
            onClick={() =>
              pushedPaymentButton(siteID, props.gwInfo['gateway-id'])
            }
            className="operation__card__content__applyButton"
            buttonText={trans('BillingSetting.buttonTxt.payment')}
          />
        );
      }

      // 課金済み かつ 他人が支払いユーザ かつ 自身が権限譲渡対象の場合
      if (userID === props.gwInfo['billing-transfer-user']) {
        // 権限譲渡承認ダイアログ表示ボタン
        return (
          <PrimaryButton
            onClick={() => openConsentDialog(props.gwInfo['gateway-id'])}
            className="operation__card__content__applyButton"
            buttonText={trans('BillingSetting.buttonTxt.transferAccept')}
          />
        );
      }

      // 課金済み かつ 自身が支払いユーザの場合
      if (props.gwInfo['billing-user-id'] === userID) {
        // ポータルボタンと歯車ボタン
        return (
          <PrimaryButton
            onClick={() =>
              pushedPortalButton(siteID, props.gwInfo['gateway-id'])
            }
            className="operation__card__content__applyButton"
            buttonText={trans('BillingSetting.buttonTxt.portal')}
            apiWait={disabledButtons[props.gwInfo['gateway-id']]}
            disabled={disabledButtons[props.gwInfo['gateway-id']]}
          />
        );
      }

      // 課金済み かつ 他人が支払いユーザ かつ 権限譲渡対象外の場合
      // グレーアウトしたポータルボタン
      return (
        <PrimaryButton
          onClick={() => pushedPortalButton(siteID, props.gwInfo['gateway-id'])}
          className="operation__card__content__applyButton"
          buttonText={trans('BillingSetting.buttonTxt.portal')}
          // 支払いユーザでないためボタン無効化
          disabled={true}
        />
      );
    };

    // 課金設定歯車ボタンの表示内容決定
    const BillingSettingGearButton = (props: {
      gwInfo: apiIF.resParameters.gatewayInformations;
    }) => {
      // 課金済み かつ 自身が支払いユーザの場合
      if (props.gwInfo['billing-user-id'] === userID) {
        // ポータルボタンと歯車ボタン
        return (
          <GearMenu
            id={`mv-btnBillingSettingOpen${props.gwInfo['gateway-id']}`}
            menuItem={gearMenuItem(props.gwInfo['gateway-id'])}
          />
        );
      } else {
        return <></>;
      }
    };

    /** 支払いボタン押下時処理
     * @param siteID : string | undefined　サイトID
     * @param gwID : srting　ゲートウェイID
     */
    const pushedPaymentButton = async (
      siteID: string | undefined,
      gwID: string
    ) => {
      try {
        if (siteID === undefined || gwID === undefined) {
          return;
        }
        //ユーザ所在国が未設定の場合はメッセージを表示
        const userInfo = await getSettingUser();
        if (userInfo.country === '') {
          alert(trans('BillingSetting.alertMsg.noContry'));
          return;
        }
        setSelectedGW(gwID);

        // VAT番号登録ダイアログの表示
        setIsOpenAddVATNumberDialog(true);
      } catch (err) {}
    };

    /** ポータル遷移ボタン押下時処理
     * @param siteID : string | undefined　サイトID
     * @param gwID : srting　ゲートウェイID
     */
    const pushedPortalButton = (siteID: string | undefined, gwID: string) => {
      if (siteID === undefined || gwID === undefined) {
        return;
      }
      setDisabledButtons(prevState => ({...prevState, [gwID]: true}));
      setSelectedGW(gwID);
      getBillingCustomerPortal(siteID, gwID)
        .then(response => {
          window.location.href = response.url;
        })
        .catch(() => {
          setIsOpenErrorDialog(false);
        })
        .finally(() => {
          setDisabledButtons(prevState => ({...prevState, [gwID]: false}));
        });
    };

    return (
      <Grid container>
        <Card className="billingTable__data__text">
          <CardContent>
            <Stack spacing={4}>
              <Grid container className="billingTable__data__text__header">
                <Grid item xs={1}>
                  {/* 課金状態によって表示するアイコンを変更する */}
                  {props.gwInfo['billing-status'] === 'paid' ||
                  props.gwInfo['billing-status'] === 'canceled' ? (
                    <img
                      className="billingTable__data__statusIcon__successMark"
                      src={iconBlack.SUCCESS.src}
                      alt={`${trans(
                        `BillingSetting.image.alternativeText.${iconBlack.SUCCESS.alt}`
                      )}`}
                    />
                  ) : (
                    <img
                      className="billingTable__data__statusIcon__failedMark"
                      src={iconBlack.FAILURE.src}
                      alt={`${trans(
                        `BillingSetting.image.alternativeText.${iconBlack.FAILURE.alt}`
                      )}`}
                    />
                  )}
                </Grid>
                <Grid item xs={10} className="billingTable__data__text__gwName">
                  {props.gwInfo['gateway-name']}
                </Grid>
                <Grid item xs={1}>
                  <BillingSettingGearButton gwInfo={props.gwInfo} />
                </Grid>
              </Grid>
              <Box>
                <Stack
                  spacing={2}
                  className="billingTable__data__text__information">
                  <CardContents
                    class="billingTable__data__text__border"
                    valueDescription={trans(
                      `BillingSetting.table.header.status`
                    )}
                    value={
                      <BillingStatus status={props.gwInfo['billing-status']} />
                    }
                  />
                  <CardContents
                    class="billingTable__data__text__border"
                    valueDescription={trans(
                      'BillingSetting.table.header.expiration'
                    )}
                    value={String(
                      props.gwInfo['billing-expiration-date'] === ''
                        ? '--'
                        : trans('BillingSetting.expirationDate.format', {
                            year: localDate.getFullYear(),
                            month: monthAbbreviationTable[localDate.getMonth()],
                            date: String(localDate.getDate()).padStart(2, '0'),
                          })
                    )}
                  />
                </Stack>
              </Box>
              <Box>
                <Box className="billingTable__data__setting">
                  <BillingSettingButton gwInfo={props.gwInfo} />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        {/* 選択したゲートウェイのダイアログのみ表示 */}
        {props.gwInfo['gateway-id'] === selectedGW ? (
          <>
            <AuthorizationTransferDialog
              gwID={props.gwInfo['gateway-id']}
              userID={userID}
              userEmail={userEmail}
              transferUser={props.gwInfo['billing-transfer-user']}
              setGWInfo={setGWInfo}
              isOpenDialog={isOpenTransferDialog}
              setIsOpenDialog={setIsOpenTransferDialog}
              setMessage={setMessage}
              setIsTransferMessageDialog={setIsTransferMessageDialog}
            />
            <ApplyCouponDialog
              gwID={props.gwInfo['gateway-id']}
              isOpenDialog={isOpenCouponDialog}
              setIsOpenDialog={setIsOpenCouponDialog}
            />
            <TransferConsentDialog
              gwID={props.gwInfo['gateway-id']}
              setGWInfo={setGWInfo}
              isOpenDialog={isOpenConsentDialog}
              setIsOpenDialog={setIsOpenConsentDialog}
              setMessage={setMessage}
              setIsTransferMessageDialog={setIsTransferMessageDialog}
            />
            <SettingVATNumberDialog
              gwID={props.gwInfo['gateway-id']}
              isOpenDialog={isOpenAddVATNumberDialog}
              setIsOpenDialog={setIsOpenAddVATNumberDialog}
            />
            <UpdateVATNumberDialog
              gwID={props.gwInfo['gateway-id']}
              isOpenDialog={isOpenUpdateVATNumberDialog}
              setIsOpenDialog={setIsOpenUpdateVATNumberDialog}
            />
          </>
        ) : (
          <></>
        )}
      </Grid>
    );
  };

  if (gwInfo === undefined) return <></>;

  return (
    // ゲートウェイ一覧の表示
    <Grid container className="billingTable">
      {/* ゲートウェイが未登録の場合は一覧を表示しない */}
      {gwInfo['gateway-informations'].length > 0 ? (
        <Box>
          <Stack spacing={4}>
            {/* ゲートウェイ一覧の描画 */}
            {gwInfo['gateway-informations'].map(
              (info: apiIF.resParameters.gatewayInformations) => {
                return (
                  <React.Fragment key={info['gateway-id']}>
                    <GatewayBillingData gwInfo={info} />
                  </React.Fragment>
                );
              }
            )}
          </Stack>
        </Box>
      ) : (
        <></>
      )}
      <TransferMessageDialog
        open={isTransferMessageDialog}
        setOpen={setIsTransferMessageDialog}
        message={message}
        id={`TransferMessageDlgBtn`}
      />
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`BillingErrDlgBtn`}
      />
    </Grid>
  );
};

export default BillingInformationTable;
